/**
 * Character Data Factory
 *
 * Generates default CharacterData objects with glossary-derived stats.
 * Useful for tests, reset functionality, and other code paths that need
 * default values.
 */

import { CHARACTER_DATA_VERSION, CharacterCollection, CharacterData } from "~/types/storage";
import { getCoreStats } from "~/utils/glossaryStats";
import { generateUUID } from "~/utils/uuid";

/**
 * Creates a default CharacterData object with glossary-derived core stats.
 *
 * Default character: Carmine Rose
 * An older, battle-hardened version of Ruby Rose. After losing her eye in
 * a fight with Salem, her powers weakened. She now wields dual scythes
 * (Bloom and Scatter) that transform into handguns, fighting with a style
 * learned from the Grimm Reaper - adapted to fight human enemies.
 *
 * @returns Default CharacterData with current format version
 */
export function createDefaultCharacterData(): CharacterData {
  const CORE_STAT_ENTRIES = getCoreStats();

  const defaultCoreStats: Record<string, number> = {};
  CORE_STAT_ENTRIES.forEach((entry) => {
    defaultCoreStats[entry.id] = 2; // Default base
  });

  // Carmine Rose's stats - experienced huntress, battle-tested
  if (defaultCoreStats["Mind"] !== undefined) {
    defaultCoreStats["Mind"] = 4; // Years of tactical experience, wiser from loss
  }
  if (defaultCoreStats["Body"] !== undefined) {
    defaultCoreStats["Body"] = 3; // Battle-hardened, durable
  }
  if (defaultCoreStats["Reflex"] !== undefined) {
    defaultCoreStats["Reflex"] = 4; // Dual-wielding scythe fighter, trained by the Grimm Reaper
  }

  return {
    id: generateUUID(),
    version: CHARACTER_DATA_VERSION,
    name: "Carmine Rose (25)",
    coreStats: defaultCoreStats,
    currentHp: 9, // Body + Body + 3
    currentResilience: 7, // Mind + Body
    strain: 0,
    items: [
      {
        id: generateUUID(),
        content: `[Item] Bloom (Hand Scythe / Handgun)
One half of Carmine's twin weapons. A compact scythe that transforms into a semi-automatic handgun. Built for fighting human enemies rather than Grimm.

### Scythe Form
- Accuracy: [Accuracy]
- Max Range: [Close]
- Damage: [Body+1]

### Handgun Form
- Accuracy: [Accuracy] + 1
- Max Range: [Medium]
- Damage: [Body]

*"I needed to learn something else to fight the more… human enemies."*`,
      },
      {
        id: generateUUID(),
        content: `[Item] Scatter (Hand Scythe / Handgun)
The twin to Bloom. Together they allow Carmine to fight with the deadly grace of the Grimm Reaper - her mother's fighting style, now her own.

### Scythe Form
- Accuracy: [Accuracy]
- Max Range: [Close]
- Damage: [Body+1]

### Handgun Form
- Accuracy: [Accuracy] + 1
- Max Range: [Medium]
- Damage: [Body]

*"While Crescent Rose is the best weapon ever, that's at slaying Grimm."*`,
      },
      {
        id: generateUUID(),
        content: `[Ability] Petal Burst (Advanced)
Carmine can disassemble objects or enemies into petals and reassemble them as she sees fit — or leave them scattered.

### Disassembly
- Accuracy: [Perception]
- Max Range: [Close]
- Effect: On success, the target is reduced to petals and cannot act until reassembled.

### Reassembly
- Accuracy: [Knowledge]
- Max Range: [Close]
- Effect: Restore a disassembled target in a form of Carmine's choosing.`,
      },
    ],
    // Profile fields
    appearance: `Silver eyes - one scarred and blind from her battle with Salem. Dark hair with red tips, now cropped shorter for practicality. Wears a weathered huntress cloak over combat-ready attire. Her expression carries the weight of loss, though determination remains in her gaze.`,
    relationships: [
      {
        id: generateUUID(),
        name: "Jaune Arc",
        content: "Took a blow meant for her. She doesn't know if he survived.",
      },
      {
        id: generateUUID(),
        name: "Summer Rose",
        content: "Her mother. \"The Grimm Reaper\" whose fighting style Carmine now wields.",
      },
    ],
    notes: `No longer the jovial girl she once was. The loss of her eye weakened her silver-eyed powers, forcing her to adapt. She built Bloom and Scatter to fight human enemies - a departure from Crescent Rose's Grimm-slaying design.

*"We were losing. She was so much stronger than we anticipated."*`,
  };
}


export function createDefaultCharacterCollectionData(): CharacterCollection {
  const defaultChar = createDefaultCharacterData();
  return {
    activeCharacterId: defaultChar.id,
    characters: [defaultChar],
  };
}
